import { useEffect, useMemo, useRef } from 'react'
import { readFoodSafetyLinkedCheck } from 'src/utils/ollieActionDeepLinks'
import { venueHomeReturnPath } from 'src/utils/venueHomeApi'

/**
 * Ollie's "Do the check" lands on one card (`?check=<definitionId>`).
 * Returns that link (or null) and scrolls/focuses the card once it has
 * loaded. `onLanded(id)` (Hub / Ollie) should focus the check; otherwise
 * the card is scrolled into view. `cardTestId(id)` is the card's `data-testid`.
 */
const useFoodSafetyLinkedCheck = ({
  search,
  items = [],
  cardTestId,
  onLanded,
  itemsReady = false,
  venueId,
  history,
}) => {
  const linked = useMemo(() => readFoodSafetyLinkedCheck(search), [search])
  const scrolledFor = useRef(null)
  const linkedId = linked?.definitionId || null
  const hasCard = Boolean(
    linkedId &&
      items.some(item => String(item?.definition?._id || '') === linkedId),
  )

  useEffect(() => {
    if (!hasCard || scrolledFor.current === linkedId) return
    if (typeof document === 'undefined') return
    const card = document.querySelector(
      `[data-testid="${cardTestId(linkedId)}"]`,
    )
    if (!card) return
    scrolledFor.current = linkedId
    card.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    if (typeof onLanded === 'function') {
      onLanded(linkedId)
      return
    }
    card.focus?.()
  }, [hasCard, linkedId, cardTestId, onLanded])

  useEffect(() => {
    if (!itemsReady || !linkedId || hasCard) return
    const back = venueHomeReturnPath(venueId, search)
    if (!back || !history?.replace) return
    history.replace(back)
  }, [itemsReady, linkedId, hasCard, venueId, search, history])

  return linked
}

export default useFoodSafetyLinkedCheck
